import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  Building2, Factory, ArrowRight, 
  TrendingUp, Package, LogOut, User, Shirt, Users, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useApp();

  const apps = [
    {
      id: 'finanzas',
      title: 'Finanzas',
      subtitle: 'Gestión Financiera',
      description: 'Compras, Ventas, Pagos, Conciliación Bancaria, Nómina y más',
      icon: TrendingUp,
      color: 'from-indigo-500 to-indigo-700',
      hoverColor: 'hover:from-indigo-600 hover:to-indigo-800',
      bgLight: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      path: '/finanzas',
      accessKey: 'acceso_finanzas'
    },
    {
      id: 'produccion',
      title: 'Producción',
      subtitle: 'Control de Producción',
      description: 'Registros, Modelos, Inventario FIFO, Movimientos y Reportes',
      icon: Factory,
      color: 'from-emerald-500 to-emerald-700',
      hoverColor: 'hover:from-emerald-600 hover:to-emerald-800',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      path: '/produccion',
      accessKey: 'acceso_produccion'
    },
    {
      id: 'muestras',
      title: 'Muestras',
      subtitle: 'Desarrollo de Muestras',
      description: 'Bases, Tizados, Fichas Técnicas, Control de Desarrollo',
      icon: Shirt,
      color: 'from-purple-500 to-purple-700',
      hoverColor: 'hover:from-purple-600 hover:to-purple-800',
      bgLight: 'bg-purple-50',
      textColor: 'text-purple-600',
      path: '/muestras',
      accessKey: 'acceso_muestras'
    }
  ];

  const handleAppClick = (app) => {
    // Verificar acceso
    if (user?.rol !== 'ADMIN' && !user?.[app.accessKey]) {
      return;
    }
    navigate(app.path);
  };

  const hasAccess = (app) => {
    if (user?.rol === 'ADMIN') return true;
    return user?.[app.accessKey];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">FinProd</h1>
                <p className="text-xs text-gray-500">Sistema Empresarial</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {user?.rol === 'ADMIN' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/usuarios')}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Usuarios
                </Button>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span>{user?.nombre || user?.username}</span>
                {user?.rol === 'ADMIN' && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                    Admin
                  </span>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={logout}
                className="text-gray-500 hover:text-gray-700"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Welcome Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Bienvenido, {user?.nombre?.split(' ')[0] || user?.username}
          </h2>
          <p className="text-gray-500 text-lg">
            Selecciona la aplicación a la que deseas acceder
          </p>
        </div>

        {/* Apps Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {apps.map((app) => {
            const Icon = app.icon;
            const canAccess = hasAccess(app);
            
            return (
              <div
                key={app.id}
                onClick={() => canAccess && handleAppClick(app)}
                className={`
                  relative group rounded-2xl overflow-hidden transition-all duration-300
                  ${canAccess 
                    ? 'cursor-pointer hover:shadow-2xl hover:-translate-y-1' 
                    : 'opacity-50 cursor-not-allowed'
                  }
                `}
              >
                {/* Card Background */}
                <div className={`
                  absolute inset-0 bg-gradient-to-br ${app.color} ${canAccess ? app.hoverColor : ''}
                  transition-all duration-300
                `} />
                
                {/* Card Content */}
                <div className="relative p-8 text-white">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-1">{app.title}</h3>
                  <p className="text-white/80 text-sm mb-4">{app.subtitle}</p>
                  
                  {/* Description */}
                  <p className="text-white/70 text-sm leading-relaxed mb-6">
                    {app.description}
                  </p>
                  
                  {/* Action */}
                  {canAccess ? (
                    <div className="flex items-center gap-2 text-white/90 group-hover:text-white transition-colors">
                      <span className="font-medium">Entrar</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-white/60">
                      <span className="text-sm">Sin acceso</span>
                    </div>
                  )}
                </div>

                {/* Decorative Elements */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
                <div className="absolute -top-10 -left-10 w-32 h-32 bg-white/5 rounded-full" />
              </div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Sistema', value: 'Activo', color: 'text-green-600' },
            { label: 'Empresa', value: user?.empresa_nombre || 'Todas', color: 'text-gray-600' },
            { label: 'Rol', value: user?.rol || 'Usuario', color: 'text-indigo-600' },
            { label: 'Versión', value: '2.0', color: 'text-gray-600' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 text-center border border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wider">{stat.label}</p>
              <p className={`text-lg font-semibold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 py-4 text-center text-xs text-gray-400">
        © 2026 FinProd - Sistema Empresarial Integrado
      </footer>
    </div>
  );
};

export default LandingPage;
